import { useDataLayer } from '@/hooks/useDataLayer';
import { useTrackPromotion } from '@/hooks/useTrackPromotion';
import type { Ga4Promotion } from '@/types';
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

interface SitewideMessagingBannerData {
  id: string;
  promotionName?: string;
  creativeName?: string;
  dismissible?: boolean;
}

const STORAGE_KEY = 'easons.nfp01.dismissed';

const readDismissed = (): string[] => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
};

const writeDismissed = (id: string): void => {
  try {
    const ids = readDismissed();
    if (!ids.includes(id)) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...ids, id]));
    }
  } catch {
    // sessionStorage unavailable (private mode etc.) — dismissal just won't persist.
  }
};

/**
 * N-FP-01 Sitewide Messaging Banner React hydration. The banner body + links are
 * server-rendered (see Views/Shared/Components/SitewideMessagingBannerBlock/Default.cshtml);
 * this mounts beside them and adds two things over the server markup:
 *  - GA4 `view_promotion` (banner in view) + `select_promotion` (image/CTA link clicked), and
 *  - the optional dismiss (×) affordance.
 * The host banner is resolved with `closest('.nfp01')`; `promotion_id` is the banner content id.
 */
export const SitewideMessagingBanner = ({
  data,
}: {
  data: SitewideMessagingBannerData;
}) => {
  const { id, promotionName, creativeName, dismissible } = data;
  const markerRef = useRef<HTMLSpanElement>(null);
  const hostRef = useRef<HTMLElement | null>(null);
  const [ready, setReady] = useState(false);
  const [hidden, setHidden] = useState(false);

  const promotion = useMemo<Ga4Promotion>(
    () => ({
      promotion_id: id,
      promotion_name: promotionName || 'Sitewide banner',
      creative_name: creativeName,
      creative_slot: 'sitewide-banner',
    }),
    [id, promotionName, creativeName],
  );

  const { onSelect } = useTrackPromotion(
    hostRef,
    ready && id && !hidden ? [promotion] : [],
  );
  const { dismissPromotion } = useDataLayer();

  // Resolve host, then apply any persisted dismissal before paint.
  useLayoutEffect(() => {
    const host = markerRef.current?.closest<HTMLElement>('.nfp01') ?? null;
    hostRef.current = host;
    if (id && host && readDismissed().includes(id)) {
      host.style.display = 'none';
      setHidden(true);
    }
    setReady(true);
  }, [id]);

  // select_promotion on the server-rendered image/CTA links.
  useEffect(() => {
    const host = hostRef.current;
    if (!ready || !host || hidden) {
      return;
    }
    const links = host.querySelectorAll<HTMLElement>(
      '.nfp01-imagelink, .nfp01-cta',
    );
    const handler = () => onSelect(promotion);
    for (const link of links) {
      link.addEventListener('click', handler);
    }
    return () => {
      for (const link of links) {
        link.removeEventListener('click', handler);
      }
    };
  }, [ready, hidden, onSelect, promotion]);

  const handleDismiss = () => {
    if (id) {
      writeDismissed(id);
      dismissPromotion(promotion);
    }
    if (hostRef.current) {
      hostRef.current.style.display = 'none';
    }
    setHidden(true);
  };

  return (
    <>
      {/* Hidden anchor so we can resolve the server-rendered host via closest(). */}
      <span ref={markerRef} style={{ display: 'none' }} aria-hidden="true" />
      {dismissible && id && !hidden ? (
        <button
          type="button"
          className="nfp01-dismiss"
          aria-label="Dismiss banner"
          onClick={handleDismiss}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      ) : null}
    </>
  );
};
